import React, { useEffect } from 'react';
import ReactApexChart from 'react-apexcharts';

const ageGroups = [
    { range: '3 to 8' },
    { range: '8 to 11' },
    { range: '11 to 14' },
    { range: '14 to 18' },
    { range: '18+' }
];

const themes = [
    'Adopt Healthy Lifestyles', 
    'Adopt sustainable food systems', 
    'Reduce E-Waste', 
    'Reduce Waste', 
    'Save Energy', 
    'Save Water', 
    'Say No to Single Use Plastic'
];

const ApexChartHeatmap = () => {
    const [selectedData, setSelectedData] = React.useState(null);
    const [participantCount, setParticipantCount] = React.useState(0);
    const chartRef = React.useRef(null);

    const fetchSpecificData = async (theme_name, age_group) => {
        try {
            const response = await fetch('https://ecoclubs.education.gov.in/api/specific-ThemeAgeGroupData', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ theme_name, age_group }),
            });

            if (response.ok) {
                const data = await response.json();
                setParticipantCount(data[0].total_participants);
            } else {
                console.error('Error fetching data: HTTP status', response.status);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const [chartOptions, setChartOptions] = React.useState({
        series: themes.map((theme) => ({
            name: theme,
            data: ageGroups.map((group) => ({
                x: group.range,
                y: 0,  
            })),
        })),
        options: {
            chart: {
                height: 350,
                type: 'heatmap',
                events: {
                    // Capture click on any data point (tile)
                    dataPointSelection: (event, chartContext, config) => {
                        const selectedTheme = config.seriesIndex;
                        const selectedAgeGroup = config.dataPointIndex;

                        // Get the corresponding age group and theme
                        const selectedAgeRange = ageGroups[selectedAgeGroup]?.range;
                        const selectedThemeName = themes[selectedTheme];

                        console.log('Selected theme:', selectedThemeName);
                        console.log('Selected age group:', selectedAgeRange);

                        fetchSpecificData(selectedThemeName, selectedAgeRange);

                        setSelectedData({
                            theme: selectedThemeName,
                            ageGroup: selectedAgeRange,
                        });

                        // Find the participant count
                        const selectedSeries = chartOptions.series[selectedTheme];
                        const selectedDataPoint = selectedSeries.data[selectedAgeGroup];

                        
                    },
                },
            },
            plotOptions: {
                heatmap: {
                    shadeIntensity: 0.5,
                    radius: 0,
                    useFillColorAsStroke: false,
                    colorScale: {
                        ranges: [],
                    },
                },
            },
            dataLabels: {
                enabled: true,
            },
            stroke: {
                width: 1,
            },
            title: {
                text: 'Eco Clubs for Mission LiFE theme vs Age group',
                align: 'left',
                offsetX: 40,
                style: {
                    fontSize: '20px',
                    fontWeight: 'bold'
                }
            },
            tooltip: {
                enabled: false,
            },
            xaxis: {
                type: 'category',
                categories: ageGroups.map(group => group.range),
                title: {
                    text: 'Age Groups',
                },
                labels: {
                    style: {
                        fontSize: '10px',
                    },
                },
            },
            yaxis: {
                title: {
                    text: 'Themes',
                },
                categories: themes,
                labels: {
                    style: {
                        fontSize: '10px',
                    },
                    offsetX: -1,
                    formatter: (value) => value.length > 20 ? value.slice(0, 20) + '...' : value
                },
            },
        },
    });

    const generateColorScale = (minValue, maxValue) => {
        const rangeStep = Math.ceil((maxValue - minValue) / 5);
        return [
            { from: 0, to: 0, name: 'Zero', color: '#808080' },
            { from: minValue, to: minValue + rangeStep, name: 'Low', color: '#FFB300' },
            { from: minValue + rangeStep + 1, to: minValue + 2 * rangeStep, name: 'Moderate', color: '#FFD700' },
            { from: minValue + 2 * rangeStep + 1, to: minValue + 3 * rangeStep, name: 'Medium', color: '#BDB200' },
            { from: minValue + 3 * rangeStep + 1, to: minValue + 4 * rangeStep, name: 'High', color: '#FF4500' },
            { from: minValue + 4 * rangeStep + 1, to: maxValue, name: 'Extreme', color: '#FF0000' },
        ];
    };

    const fetchData = async (selectedTheme = null, selectedAgeGroup = null) => {
        try {
            const response = await fetch('https://ecoclubs.education.gov.in/api/heat-map-age-theme', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({ selectedTheme, selectedAgeGroup }),
            });

            if (response.ok) {
                const data = await response.json();

                let minValue = Infinity;
                let maxValue = -Infinity;

                const updatedSeries = themes.map((theme) => {
                    const dataForTheme = data.find(item => item.theme_name === theme);

                    if (dataForTheme) {
                        const updatedData = ageGroups.map(group => {
                            let participantCountForAgeGroup = 0;

                            switch (group.range) {
                                case '3 to 8':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age3to8 || 0;
                                    break;
                                case '8 to 11':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age8to11 || 0;
                                    break;
                                case '11 to 14':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age11to14 || 0;
                                    break;
                                case '14 to 18':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age14to18 || 0;
                                    break;
                                case '18+':
                                    participantCountForAgeGroup = dataForTheme.total_participant_age18plus || 0;
                                    break;
                                default:
                                    participantCountForAgeGroup = 0;
                            }

                            minValue = Math.min(minValue, participantCountForAgeGroup);
                            maxValue = Math.max(maxValue, participantCountForAgeGroup);

                            return {
                                x: group.range,
                                y: participantCountForAgeGroup,
                            };
                        });

                        return { name: theme, data: updatedData };
                    }

                    return {
                        name: theme,
                        data: ageGroups.map(group => ({
                            x: group.range,
                            y: 0,
                        })),
                    };
                });

                const dynamicColorRanges = generateColorScale(minValue, maxValue);

                setChartOptions((prevOptions) => ({
                    ...prevOptions,
                    series: updatedSeries,
                    options: {
                        ...prevOptions.options,
                        plotOptions: {
                            ...prevOptions.options.plotOptions,
                            heatmap: {
                                ...prevOptions.options.plotOptions.heatmap,
                                colorScale: {
                                    ranges: dynamicColorRanges,
                                },
                            },
                        },
                    },
                }));

                if (selectedTheme && selectedAgeGroup) {
                    const selectedSeries = updatedSeries.find(series => series.name === selectedTheme);
                    const selectedDataPoint = selectedSeries.data.find(dataPoint => dataPoint.x === selectedAgeGroup);
                    setParticipantCount(selectedDataPoint ? selectedDataPoint.y : 0);
                }
            } else {
                console.error('Failed to fetch data: ', response.statusText);
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const resetSelection = () => {
        setSelectedData(null);
        setParticipantCount(0); 
        if (chartRef.current) {
            chartRef.current.chart.updateSeries(chartOptions.series); 
        }
    };

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <div style={{ position: 'relative', marginBottom: '22px' }}>
            <div id="chart">
                <ReactApexChart
                    ref={chartRef}  
                    options={chartOptions.options}
                    series={chartOptions.series}
                    type="heatmap"
                    height={350}
                />
            </div>

            {selectedData && (
                <div 
                    style={{
                        marginTop: '20px',
                        padding: '15px',
                        backgroundColor: '#f9f9f9',
                        border: '1px solid #ddd',
                        borderRadius: '8px',
                        boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
                        maxWidth: '400px',
                        margin: '20px auto',
                    }}
                >
                    <h4 
                        style={{
                            fontSize: '18px',
                            fontWeight: '600',
                            marginBottom: '10px',
                            color: '#333',
                            textAlign: 'center',
                        }}
                    >
                        Selected Age Group and Theme
                    </h4>

                    <div 
                        style={{
                            fontSize: '16px',
                            lineHeight: '1.5',
                            color: '#555',
                            padding: '5px 10px',
                            textAlign: 'left',
                        }}
                    >
                        <p 
                            style={{
                                margin: '5px 0',
                            }}
                        >
                            <strong>Age Group:</strong> {selectedData.ageGroup}
                        </p>

                        <p 
                            style={{
                                margin: '5px 0',
                            }}
                        >
                            <strong>Theme:</strong> {selectedData.theme}
                        </p>

                        <p 
                            style={{
                                margin: '5px 0',
                            }}
                        >
                            <strong>Total People in Age Group:</strong> {participantCount}
                        </p>
                    </div>
                </div>
            )}

            <button className='resetButtonbtn'
                onClick={resetSelection} 
                style={{ 
                    position: 'absolute', 
                    top: '1px', 
                    right: '50px', 
                    padding: '5px 10px',  
                    cursor: 'pointer',
                    fontSize: '12px',  
                    backgroundColor: '#f44336',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px'
                }}
             >
                Reset
            </button>
        </div>
    );
};

export default ApexChartHeatmap;
